import { AppBar, Box, Button, Card, Checkbox, Collapse, debounce, Dialog, DialogActions, DialogContent, DialogContentText, Divider, FormControlLabel, Grid, styled, Switch, TextField, Toolbar, Typography } from "@mui/material"
import { useAppSelector } from "../../../../hooks/store-hooks"
import { usePerfilService } from "../../../../../use-cases/perfil"
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useEffect, useRef, useState } from "react";
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import { ITipoUsuario } from "../../../../../application/models";

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export const FormEditPerfil = () => {
  // State
  const { editPerfilForm, openEditPerfilForm, editPerfilProdutosVinculados, perfilProdutosVinculados } = useAppSelector((state) => state.perfil)
  const { currentUser } = useAppSelector((state) => state.user)
  // Useful variables
  const isDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS
  const isTecnologiaDaInformacao = currentUser?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO
  const isCliente = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE
  const canEditPerfil = isDemais || isTecnologiaDaInformacao || (isCliente && editPerfilForm.fields.idCliente === currentUser?.IdCliente)
  // Use State
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
  const [searchTerms, setSearchTerms] = useState<{ [key: number]: string }>({});
  const hasAppliedDefaultsRef = useRef(false)
  const lastPerfilIdRef = useRef<number | null>(null)
  const hasLoadedFromApiRef = useRef(false)

  // Service
  const {
    setOpenEditPerfilForm,
    setEditPerfilFormName,
    setEditPerfilFormStatus,
    updatePerfil,
    setEditPerfilVincularProduto,
    setRemoveEditPerfilProduto,
    setEditPerfilVincularTipoConsumo,
    getProdutosVinculados,
    aplicarTiposConsumoPadraoPerfil
  } = usePerfilService()

  useEffect(() => {
    if (!openEditPerfilForm) return
    if (hasLoadedFromApiRef.current) return
    hasLoadedFromApiRef.current = true

    getProdutosVinculados()
  }, [openEditPerfilForm, editPerfilForm.fields.idPerfil, editPerfilForm.fields.idCliente, getProdutosVinculados])

  useEffect(() => {
    const idPerfil = editPerfilForm.fields.idPerfil
    if (lastPerfilIdRef.current !== idPerfil) {
      lastPerfilIdRef.current = idPerfil
      hasAppliedDefaultsRef.current = false
      hasLoadedFromApiRef.current = false
    }
  }, [editPerfilForm.fields.idPerfil])

  useEffect(() => {
    if (!perfilProdutosVinculados) return
    if (perfilProdutosVinculados.length === 0) return

  }, [perfilProdutosVinculados, editPerfilProdutosVinculados])

  useEffect(() => {
    aplicarTiposConsumoPadraoPerfil(hasAppliedDefaultsRef);
  }, [aplicarTiposConsumoPadraoPerfil]);

  // Function
  const handleClose = () => {
    setOpenEditPerfilForm({
      open: false
    })
  }

  const handleExpandClick = (key: string) => {
    setExpanded((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  return (
    <>
      <Dialog
        open={openEditPerfilForm}
        onClose={handleClose}
        scroll={'paper'}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        fullScreen
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1 }} textTransform={"uppercase"} fontWeight={"bold"} variant="h6" component="div">
              Editar perfil
            </Typography>
            <Button
              color="inherit"
              startIcon={<>
                <CloseIcon />
              </>}
              onClick={handleClose}
              aria-label="close"
            >
              Fechar
            </Button>
          </Toolbar>
        </AppBar>
        <DialogContent dividers={true}>
          <DialogContentText id="scroll-dialog-description" tabIndex={-1}></DialogContentText>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                defaultValue={editPerfilForm.fields.nome}
                disabled={!canEditPerfil}
                onChange={debounce((e) => {
                  setEditPerfilFormName({
                    nome: e.target.value
                  })
                }, 700)}
                error={Boolean(editPerfilForm.touched && editPerfilForm.errors.find(item => item.key === 'nome'))}
                helperText={editPerfilForm.errors.find(item => item.key === 'nome')?.message}
                name="name"
                fullWidth
                id="name"
                label="Name"
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                disabled={!canEditPerfil}
                control={
                  <Switch
                    checked={editPerfilForm.fields.status}
                    onChange={(e) => {
                      setEditPerfilFormStatus({
                        status: e.target.checked
                      })
                    }}
                    name="status"
                    color="primary"
                  />
                }
                label={editPerfilForm.fields.status ? 'Ativo' : 'Inativo'}
              />
            </Grid>
            <Grid item xs={12}>
              <Card variant='outlined'>
                {perfilProdutosVinculados.map((produto, i) => (
                  <Box
                    key={i}
                    my={2}
                  >
                    <Box
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <FormControlLabel
                        disabled={!canEditPerfil}
                        control={
                          <Checkbox
                            color="secondary"
                            checked={editPerfilProdutosVinculados.find((perfilProduto) => {
                              if (
                                produto.Produto.IdProduto === perfilProduto.Produto.IdProduto
                                && perfilProduto.Produto.Ativo
                              ) {
                                return perfilProduto
                              } else {
                                return false
                              }
                            })?.Produto ? true : false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const produtoCompletoAPI = perfilProdutosVinculados.find(
                                  (p) => p.Produto.IdProduto === produto.Produto.IdProduto
                                )


                                if (produtoCompletoAPI) {
                                  const produtoComAlteracoes = editPerfilProdutosVinculados.find(
                                    (p) => p.Produto.IdProduto === produto.Produto.IdProduto
                                  )


                                  const tiposConsumoMesclados = produtoCompletoAPI.TipoConsumo.map(tipoAPI => {
                                    if (produtoComAlteracoes) {
                                      const tipoExistente = produtoComAlteracoes.TipoConsumo.find(
                                        tc => tc.IdTipoConsumo === tipoAPI.IdTipoConsumo
                                      )
                                      if (tipoExistente) {
                                        return { ...tipoExistente, Ativo: true }
                                      }
                                    }
                                    return { ...tipoAPI, Ativo: true }
                                  })

                                  setEditPerfilVincularProduto({
                                    produto: produto.Produto,
                                    tiposConsumo: tiposConsumoMesclados
                                  })
                                } else {
                                  setEditPerfilVincularProduto({
                                    produto: produto.Produto,
                                    tiposConsumo: []
                                  })
                                }
                              } else {
                                setRemoveEditPerfilProduto({
                                  idProduto: produto.Produto.IdProduto
                                })
                              }
                            }}
                          />
                        }
                        label={produto.Produto.DescricaoProduto}
                        style={{ margin: 0 }}
                      />
                      <ExpandMore
                        expand={expanded[i] || false}
                        onClick={() => handleExpandClick(i.toString())}
                        aria-expanded={expanded[i] || false}
                        aria-label="show more"
                        style={{
                          transform: expanded[i] ? 'rotate(180deg)' : 'rotate(0deg)',
                          cursor: 'pointer',
                        }}
                      >
                        <ExpandMoreIcon />
                      </ExpandMore>
                    </Box>
                    <Collapse
                      in={expanded[i] || false}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Grid container>
                        <Grid item xs={12} sx={{ mb: 1, px: 2 }}>
                          <TextField
                            size="small"
                            placeholder="Pesquisar variável..."
                            variant="outlined"
                            fullWidth
                            value={searchTerms[produto.Produto.IdProduto] || ''}
                            onChange={(e) => setSearchTerms(prev => ({
                              ...prev,
                              [produto.Produto.IdProduto]: e.target.value
                            }))}
                          />
                        </Grid>
                        {produto.TipoConsumo
                          .filter(consumo =>
                            !searchTerms[produto.Produto.IdProduto] ||
                            consumo.Descricao.toLowerCase().includes(searchTerms[produto.Produto.IdProduto].toLowerCase())
                          )
                          .map((consumo) => {
                            return <Grid item xs={12} key={`${consumo.IdProduto} - ${consumo.IdTipoConsumo}`}>
                              <FormControlLabel
                                disabled={!canEditPerfil}
                                label={consumo.Descricao}
                                control={
                                  <Switch
                                    checked={(() => {
                                      const perfilProduto = editPerfilProdutosVinculados.find(
                                        (p) => p.Produto.IdProduto === produto.Produto.IdProduto
                                      )

                                      if (!perfilProduto) {
                                        return false
                                      }

                                      const tipoConsumo = perfilProduto.TipoConsumo.find(
                                        (tc) => tc.IdTipoConsumo === consumo.IdTipoConsumo
                                      )

                                      return tipoConsumo?.Ativo === true
                                    })()}
                                    onChange={(e) => {
                                      setEditPerfilVincularTipoConsumo({
                                        ativo: e.target.checked,
                                        produto: produto.Produto,
                                        tipoConsumo: consumo
                                      })
                                    }}
                                  />
                                }
                              />

                            </Grid>
                          })}
                      </Grid>
                    </Collapse>
                    <Divider />
                  </Box>
                ))}
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            onClick={updatePerfil}
            variant="contained"
            disabled={!canEditPerfil}
          >
            Atualizar
          </Button>
        </DialogActions>
      </Dialog >
    </>
  )
}
